import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '../lib/utils';
import {Icons} from '../icons';
import {DocumentIcon} from '../document-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../primitives';

export interface PendingAttachment {
  id: string;
  name: string;
  contentType: string;
  byteSize: number;
  signedBlobId?: string;
  isUploading?: boolean;
}

const attachmentsListVariants = cva('flex max-h-32 gap-1.5 overflow-y-auto', {
  variants: {
    layout: {
      // Stacked, full-width rows — default for side panels.
      column: '-mr-1.5 flex-col pr-1.5',
      // Compact chips sitting side by side, wrapping onto new rows.
      wrap: 'flex-wrap',
    },
  },
  defaultVariants: {layout: 'column'},
});

const attachmentChipVariants = cva(
  'flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-accent px-2 py-1 text-xs',
  {
    variants: {
      layout: {
        column: '',
        wrap: 'max-w-[180px]',
      },
    },
    defaultVariants: {layout: 'column'},
  },
);

export interface PromptInputAttachmentsProps
  extends VariantProps<typeof attachmentsListVariants> {
  attachments: PendingAttachment[];
  onRemove: (id: string) => void;
  className?: string;
}

export const PromptInputAttachments = React.forwardRef<
  HTMLDivElement,
  PromptInputAttachmentsProps
>(({attachments, onRemove, className, layout}, ref) => {
  if (attachments.length === 0) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={ref}
        className={cn(attachmentsListVariants({layout}), className)}
      >
        {attachments.map((attachment) => (
          <Tooltip key={attachment.id}>
            <TooltipTrigger asChild>
              <div className={attachmentChipVariants({layout})}>
                <DocumentIcon
                  contentType={attachment.contentType}
                  name={attachment.name}
                  className="shrink-0"
                />
                <span className="min-w-0 flex-1 truncate">
                  {attachment.name}
                </span>
                {attachment.isUploading ? (
                  <Icons.spinner className="size-3 shrink-0 animate-spin text-muted-foreground" />
                ) : (
                  <button
                    type="button"
                    // Keep focus on the input so removing a chip doesn't collapse the bar.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onRemove(attachment.id)}
                    aria-label={`Remove attachment ${attachment.name}`}
                    className="shrink-0 rounded-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <Icons.x className="size-3" />
                  </button>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs break-all">
              {attachment.name}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
});
PromptInputAttachments.displayName = 'PromptInputAttachments';
